import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, FileSpreadsheet } from 'lucide-react';
import { ESG_FRAMING_LINE, ESG_TARGET_LENS_HELPER } from '@siliconx/shared';
import { Card } from '@siliconx/shared/ui/Card';
import { SectionLabel } from '@siliconx/shared/ui/SectionLabel';
import { api, apiDownload, throwIfNotOk } from '../../../lib/api';
import { qk } from '../../../lib/queryKeys';

/**
 * SIL-442 (W4) — the three-file ESG export download, wired to the W3
 * (SIL-438) endpoints that already exist:
 *
 *   GET /api/client/scenarios/:id/export/esg/tidy.csv
 *   GET /api/client/scenarios/:id/export/esg/pivot.csv
 *   GET /api/client/scenarios/:id/export/esg/methodology.csv
 *
 * NO NEW EXPORT ROUTES. Every byte of these files (including the §1 framing
 * line in each header and the auto-generated methodology sheet) is produced
 * server-side by `apps/api/src/routes/client/esg.ts`; this component only
 * picks a scenario and streams the bytes to disk via `apiDownload`.
 *
 * WHY A SCENARIO PICKER ON A SCENARIO-FREE DASHBOARD: the ESG export is a
 * per-SCENARIO artifact by spec §8 ("one row per (scenario, lens,
 * reporting_year, ghg_category, cohort)"), while this dashboard is the
 * scenario-free hold-everything lens. Rather than invent an export route
 * for a synthetic "baseline scenario" that no `financial_scenario` row
 * backs — which would put an unsourceable scenario identity into the
 * methodology sheet — the download asks which saved scenario to export.
 */

const ESG_FILES = [
  {
    key: 'tidy' as const,
    path: 'tidy.csv',
    filename: 'esg-tidy.csv',
    title: 'Long / tidy CSV',
    description: 'One row per scenario, lens, reporting year, GHG category, and cohort, with its basis tag.',
  },
  {
    key: 'pivot' as const,
    path: 'pivot.csv',
    filename: 'esg-pivot.csv',
    title: 'Human pivot sheet',
    description: 'Reporting-year rows by category columns, with the organization target as its own labeled series.',
  },
  {
    key: 'methodology' as const,
    path: 'methodology.csv',
    filename: 'esg-methodology.csv',
    title: 'Methodology & assumptions sheet',
    description:
      'Auto-generated: verification caveat, factor sources and versions, data-quality statements, category assignments, timing convention, and every exclusion with its justification.',
  },
];

export function EsgExportCard() {
  const [scenarioId, setScenarioId] = useState<string>('');
  const [downloading, setDownloading] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  const scenariosQ = useQuery({
    queryKey: qk.scenarios.all,
    queryFn: async () => {
      const res = await api.api.client.scenarios.$get({ query: {} });
      await throwIfNotOk(res);
      return res.json();
    },
  });

  const scenarios = scenariosQ.data ?? [];
  const selectedId = scenarioId || scenarios[0]?.id || '';

  const handleDownload = async (fileKey: string, path: string, filename: string) => {
    if (!selectedId) return;
    setDownloading(fileKey);
    setFailed(null);
    try {
      await apiDownload(`/api/client/scenarios/${selectedId}/export/esg/${path}`, filename);
    } catch (err) {
      console.error('[esg-export] download failed:', err);
      setFailed(fileKey);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <SectionLabel className="inline-flex items-center gap-1.5">
            <FileSpreadsheet className="size-3.5 text-zinc-400" />
            ESG export
          </SectionLabel>
          <p className="mt-1 max-w-2xl text-xs text-zinc-500">
            Three files per scenario. Each carries &ldquo;{ESG_FRAMING_LINE}&rdquo; as a single quoted cell in
            its header row, and every data row carries its GHG category tag.
          </p>
        </div>

        <label className="flex items-center gap-2 text-xs text-zinc-600">
          Scenario
          <select
            value={selectedId}
            onChange={(e) => setScenarioId(e.target.value)}
            disabled={scenarios.length === 0}
            className="rounded-md border border-zinc-300 bg-white px-2 py-1.5 text-xs text-zinc-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {scenarios.length === 0 && <option value="">No saved scenarios</option>}
            {scenarios.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {scenarios.length === 0 && !scenariosQ.isLoading && (
        <p className="mt-3 rounded-md border border-dashed border-zinc-300 bg-zinc-50 px-3 py-2 text-xs text-zinc-600">
          The ESG export is produced per saved scenario. Build a scenario in Scenarios first, then return here
          to download its three files.
        </p>
      )}

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        {ESG_FILES.map((file) => (
          <div key={file.key} className="flex flex-col rounded-lg border border-zinc-200 bg-zinc-50/60 p-3">
            <div className="text-xs font-semibold text-zinc-800">{file.title}</div>
            <p className="mt-1 flex-1 text-xs leading-relaxed text-zinc-500">{file.description}</p>
            <button
              type="button"
              onClick={() => void handleDownload(file.key, file.path, file.filename)}
              disabled={!selectedId || downloading !== null}
              className="mt-3 inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-md border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Download className="size-3.5" />
              {downloading === file.key
                ? 'Downloading…'
                : failed === file.key
                  ? 'Failed — retry'
                  : 'Download'}
            </button>
          </div>
        ))}
      </div>

      {/* §12 — the export carries the organization target only. */}
      <p className="mt-3 text-xs leading-relaxed text-zinc-500">{ESG_TARGET_LENS_HELPER}</p>
    </Card>
  );
}
